import Konva from "konva";
import { KonvaEventObject } from "konva/lib/Node";
import { Stage } from "konva/lib/Stage";
import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { BoundingBoxesContext } from "context/boundingBoxesContext";

import { Point } from "../types";
import { clampPointToCanvas, rotateDimensions } from "../utils";

import { Dimensions } from "./useResize";

export function getPointerPosition(canvasRef: React.MutableRefObject<Stage | null>) {
  const point = canvasRef.current!.getRelativePointerPosition();
  if (!point) return { x: 0, y: 0 };
  return {
    x: Math.max(point.x, 0),
    y: Math.max(point.y, 0),
  };
}

interface Props {
  canvasDimensions: Dimensions;
  isRotated: boolean;
  canvasRef: React.MutableRefObject<Konva.Stage | null>;
  onEndDrawingBoundingBox: (drawingStartPoint: Point | null, drawingEndPoint: Point | null) => void;
}

export function useDrawBoundingBox({ canvasDimensions, isRotated, canvasRef, onEndDrawingBoundingBox }: Props) {
  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const [drawingStartPoint, setDrawingStartPoint] = useState<Point | null>(null);
  const [drawingEndPoint, setDrawingEndPoint] = useState<Point | null>(null);
  const { hoveredNode } = useContext(BoundingBoxesContext) ?? {};

  const startDrawingBoundingBox = useCallback(
    (e: KonvaEventObject<MouseEvent>) => {
      // cannot drag an active bounding box and create another at the same time
      if (hoveredNode !== "none" && !isDrawing) {
        return;
      }

      if (!canvasRef.current) return;

      if (e.evt.button !== 0) return; // only left click (0)

      const point = getPointerPosition(canvasRef);

      setIsDrawing(true);
      setDrawingStartPoint(point);
    },
    [hoveredNode, canvasRef, isDrawing]
  );

  const moveDrawingBoundingBox = useCallback(() => {
    if (!isDrawing || !canvasRef.current) return;

    let point = getPointerPosition(canvasRef);

    // Ensure that the point remains inside the canvas
    point = clampPointToCanvas(point, rotateDimensions(canvasDimensions, isRotated));
    setDrawingEndPoint(point);
  }, [canvasDimensions, isDrawing, isRotated, canvasRef]);

  const drawingStartPointRef = useRef(drawingStartPoint);
  const drawingEndPointRef = useRef(drawingEndPoint);

  useEffect(() => {
    drawingStartPointRef.current = drawingStartPoint;
    drawingEndPointRef.current = drawingEndPoint;
  }, [drawingStartPoint, drawingEndPoint]);

  const endDrawingBoundingBox = useCallback(() => {
    setIsDrawing(false);
    onEndDrawingBoundingBox(drawingStartPointRef.current, drawingEndPointRef.current);
    setDrawingStartPoint(null);
    setDrawingEndPoint(null);
  }, [onEndDrawingBoundingBox]);

  useEffect(() => {
    window.addEventListener("mousemove", moveDrawingBoundingBox);

    return () => {
      window.removeEventListener("mousemove", moveDrawingBoundingBox);
    };
  }, [moveDrawingBoundingBox]);
  useEffect(() => {
    window.addEventListener("mouseup", endDrawingBoundingBox);

    return () => {
      window.removeEventListener("mouseup", endDrawingBoundingBox);
    };
  }, [endDrawingBoundingBox]);

  const drawingKeyEvents = (e: KeyboardEvent) => {
    if ((e.key === "Delete" || e.key === "Backspace") && isDrawing) {
      endDrawingBoundingBox();
    }
  };

  return {
    startDrawingBoundingBox,
    moveDrawingBoundingBox,
    drawingKeyEvents,
    drawingStartPoint,
    drawingEndPoint,
  };
}
